import React from 'react';
import { Collapse } from 'antd';
import PropTypes from 'prop-types';

import { useQuery } from '@tanstack/react-query';

import { TRADEBOOK } from '../../../../utilities/functions';

import CriteriaList from './CriteriaList';

const { Panel } = Collapse;

export default function StandardFields({ plan, planId, onFieldChange }) {
    // Fetch criteria statistics
    const { data: statsData, isLoading: statsLoading } = useQuery(
        ['criteria-stats', planId],
        () => TRADEBOOK.fetchCriteriaStats(planId),
        {
            enabled: !!planId,
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
            refetchOnWindowFocus: false
        }
    );

    const criteriaStats = statsData?.criteriaStats || {};
    const totalEvaluatedTrades = statsData?.totalEvaluatedTrades || 0;

    const standardFields = [
        {
            section: 'Setup & Timing',
            fields: [
                { key: 'time', label: 'Time', placeholder: 'e.g., 9:30 AM - 11:00 AM EST' },
                {
                    key: 'indicators',
                    label: 'Indicators',
                    placeholder: 'e.g., VWAP, EMA 9/20, Volume'
                },
                {
                    key: 'confirmations',
                    label: 'Confirmations',
                    placeholder: 'e.g., Volume > 1M shares'
                },
                {
                    key: 'entrySignal',
                    label: 'Entry Signal',
                    placeholder: 'e.g., Break above previous day high'
                },
                { key: 'stopLoss', label: 'Stop Loss', placeholder: 'e.g., Below VWAP' },
                { key: 'targets', label: 'Targets', placeholder: 'e.g., 2:1 Risk/Reward minimum' }
            ]
        },
        {
            section: 'Risk Management',
            fields: [
                {
                    key: 'riskPerTrade',
                    label: 'Risk Per Trade',
                    placeholder: 'e.g., 1% of account'
                },
                {
                    key: 'profitTargetPerTrade',
                    label: 'Profit Target Per Trade',
                    placeholder: 'e.g., 2-3%'
                },
                {
                    key: 'maxLossPerTrade',
                    label: 'Max Loss Per Trade',
                    placeholder: 'e.g., 1% max'
                },
                {
                    key: 'stopLossMechanism',
                    label: 'Stop Loss Mechanism',
                    placeholder: 'e.g., Hard stop at entry - risk amount'
                }
            ]
        },
        {
            section: 'Trade Management',
            fields: [
                {
                    key: 'partialPlan',
                    label: 'Partial Plan',
                    placeholder: 'e.g., Scale out 1/3 at 1R, 1/3 at 2R'
                },
                {
                    key: 'addingToPosition',
                    label: 'Adding to Position',
                    placeholder: 'e.g., Add on pullback to EMA9'
                },
                {
                    key: 'howToManageStop',
                    label: 'How to Manage Stop',
                    placeholder: 'e.g., Move to breakeven at 1R'
                }
            ]
        },
        {
            section: 'Rules & Notes',
            fields: [
                {
                    key: 'specificRules',
                    label: 'Specific Rules',
                    placeholder: 'e.g., Only trade if market trending'
                }
            ]
        }
    ];

    return (
        <div className="standard-fields">
            <Collapse defaultActiveKey={['0']} accordion>
                {standardFields.map((section, sectionIndex) => (
                    <Panel header={section.section} key={sectionIndex}>
                        {section.fields.map((field) => (
                            <div
                                key={field.key}
                                className="field-group"
                                style={{
                                    marginLeft: '20px',
                                    marginBottom: '16px',
                                    backgroundColor: '#fafafa',
                                    padding: '8px',
                                    borderRadius: '4px'
                                }}
                            >
                                <label style={{ fontWeight: 'bold' }}>{field.label}</label>
                                <CriteriaList
                                    criteriaList={plan[field.key] || []}
                                    onChange={(newList) => onFieldChange(field.key, newList)}
                                    placeholder={field.placeholder}
                                    allowReorder
                                    // Stats props
                                    showStats={!!planId}
                                    criteriaStats={criteriaStats[field.key] || []}
                                    statsLoading={statsLoading}
                                    totalEvaluatedTrades={totalEvaluatedTrades}
                                />
                            </div>
                        ))}
                    </Panel>
                ))}
            </Collapse>
        </div>
    );
}

StandardFields.propTypes = {
    plan: PropTypes.object.isRequired,
    planId: PropTypes.string,
    onFieldChange: PropTypes.func.isRequired
};
